import { useState, useEffect, useCallback } from 'react';
import {
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { IFinancialPlan } from '../../shared/interfaces';
import { useToast } from '../../hooks/toast';
import FinancialPlanCreateEdit from './CreateEdit';
import {
  ATable,
  MenuOptions,
  StatusActive,
  DropdownOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import { getFinancialPlans } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const FinancialPlans: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [financialPlan, setFinancialPlan] = useState<IFinancialPlan | null>(
    null,
  );
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [financialPlans, setFinancialPlans] = useState<IFinancialPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const list = useCallback(async () => {
    setLoading(true);
    setFinancialPlans([]);
    try {
      const response = await getFinancialPlans(
        limit,
        (page - 1) * limit,
        search,
      );
      setPages(response.count);
      setFinancialPlans(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar planos de contas',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalFinancialPlanCreateEdit = () => {
    if (modalCreate)
      return (
        <FinancialPlanCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          financialPlan={financialPlan}
          setFinancialPlans={setFinancialPlans}
        />
      );
  };

  useEffect(() => {
    list();
  }, [page, search, limit, list]);

  return (
    <PageLayout>
      <PageIntro
        badge="Financeiro"
        icon={<UnorderedListOutlined />}
        title="Plano de contas"
        description="Estruture as categorias financeiras da clínica com uma visualização mais limpa e consistente com o restante do sistema."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => list(),
          },
          permissionFind([PermissionActions.financialsPlansCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setFinancialPlan(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          columns={[
            {
              title: 'Id',
              width: 100,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Descrição',
              dataIndex: 'description',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IFinancialPlan) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.financialsPlansUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setFinancialPlan(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={financialPlans}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalFinancialPlanCreateEdit()}
    </PageLayout>
  );
};

export default FinancialPlans;
